import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { logout, useAuth } from '@izimi/auth';
import type { NavigationMenuItem } from '@izimi/components';
import { appEnv } from '@izimi/core';
import { useModules } from '@izimi/modules';

import { Routes } from './types';

const useNavigationMenu = () => {
  const { t } = useTranslation('shared');
  const auth = useAuth();

  const {
    documents: { usePagedDocuments },
  } = useModules();
  const { totalUnseen: totalUnseenDocuments } = usePagedDocuments({
    type: 'unSeen',
  });

  const hasSingleAuthenticationProvider = auth.authenticationProviders?.length === 1;

  const mainMenu = useMemo<NavigationMenuItem<Routes>[]>(() => {
    const menuItems: NavigationMenuItem<Routes>[] = [
      {
        href: Routes.Vault,
        label: t('navigation.documents'),
        customIconName: 'documents',
        badge: totalUnseenDocuments,
      },
      {
        href: Routes.Contacts,
        label: t('navigation.contacts'),
        customIconName: 'users',
      },
      {
        href: Routes.VaultTransfer,
        label: t('shared:navigation.vaultTransfer'),
        customIconName: 'vault-transfer',
      },
      {
        href: Routes.Dossiers,
        label: t('shared:navigation.dossiers'),
        customIconName: 'folder',
      },
    ];

    if (appEnv !== 'production') {
      menuItems.push({
        href: Routes.Debug,
        label: t('navigation.debug'),
        customIconName: 'bug',
      });
    }

    return menuItems;
  }, [t, totalUnseenDocuments]);

  const helpMenu = useMemo<NavigationMenuItem<Routes>[]>(
    () => [
      {
        href: Routes.HelpInfo,
        customIconName: 'mail',
        label: t('navigation.support'),
      },
      {
        href: Routes.HelpReportProblem,
        customIconName: 'bug',
        label: t('navigation.bugReport'),
      },
    ],
    [t],
  );

  const userMenu = useMemo<NavigationMenuItem<Routes>[]>(() => {
    const logoutItem: NavigationMenuItem<Routes> = {
      href: Routes.Hash,
      customIconName: 'logout',
      label: t('navigation.logout'),
      onClick: () => logout(),
    };

    return [
      {
        href: Routes.Profile,
        customIconName: 'user',
        label: t('navigation.profile'),
      },
      {
        href: Routes.Trash,
        customIconName: 'trash',
        label: t('navigation.trashBin'),
      },
      {
        href: Routes.HistoryLog,
        customIconName: 'clock',
        label: t('navigation.history'),
      },
      ...(!hasSingleAuthenticationProvider ? [logoutItem] : []),
    ];
  }, [t, hasSingleAuthenticationProvider]);

  return {
    mainMenu,
    helpMenu,
    userMenu,
  };
};

export default useNavigationMenu;
